// API 基础配置
const API_BASE_URL = 'https://dk-backend.ibase.site/api/v1';

// 通用的API响应类型
export interface ApiResponse<T = any> {
  status: number;
  data?: T;
  msg?: string;
  error?: string;
}

// 用户类型
export interface User {
  id: number;
  username: string;
  status: string;
  is_doctor: boolean;
}

// 登录/注册响应数据类型
export interface AuthData {
  user: User;
  token: string;
}

// 登录请求参数
export interface LoginParams {
  username: string;
  password: string;
}

// 注册请求参数
export interface RegisterParams {
  username: string;
  password: string;
  password2: string;
  referer_name?: string;
}

// 修改密码请求参数
export interface ChangePasswordParams {
  username: string;
  oldpassword: string;
  newpassword: string;
  newpassword2: string;
}

// 优惠券类型
export interface Discount {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string | null;
  UserId: number;
  SourceType: number; // 1: 首次使用, 2: 邀请新用户, 3: 提出意见
  SourceDetail: string;
  Used: boolean;
  UsedAt: string;
}

// 带用户信息的优惠券类型（医生端）
export interface DiscountWithUser {
  id: number;
  created_at: string;
  updated_at: string;
  user_id: number;
  username: string;
  source_type: number; // 1: 首次使用, 2: 邀请新用户, 3: 提出意见
  source_detail: string;
  used: boolean;
  used_at: string;
}

// 获取优惠券响应数据
export interface GetDiscountData {
  discounts: Discount[];
}

// 获取所有优惠券响应数据（医生端）
export interface GetAllDiscountListData {
  discounts: DiscountWithUser[];
  total: number;
  page: number;
  page_size: number;
}

// 获取所有优惠券请求参数（医生端）
export interface GetAllDiscountListParams {
  page: number;
  page_size: number;
}

// 使用优惠券请求参数
export interface UseDiscountParams {
  discount_id: number;
}

// 建议类型
export interface Advice {
  ID: number;
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string | null;
  UserId: number;
  Advice: string;
  Accepted: boolean;
}

// 添加建议请求参数
export interface AddAdviceParams {
  advice: string;
}

// 获取建议列表响应数据
export interface GetAdviceListData {
  advices: Advice[];
}

// 接受建议请求参数
export interface AcceptAdviceParams {
  advice_id: number;
}

// 用户统计信息类型
export interface UserWithStats {
  id: number;
  username: string;
  created_at: string;
  updated_at: string;
  is_doctor: boolean;
  referer_name: string;
  conversation_count: number;
  inquiry_count: number;
  advice_count: number;
  discount_count: number;
}

// 获取所有用户列表响应数据（医生端）
export interface GetAllUserListData {
  users: UserWithStats[];
  total: number;
  page: number;
  page_size: number;
}

// 获取所有用户列表请求参数（医生端）
export interface GetAllUserListParams {
  page: number;
  page_size: number;
  search_by?: string;
}

// 重置密码请求参数（医生端）
export interface ResetPasswordParams {
  user_id: number;
}

// 舌象标签释义
export interface TongueLabelResult {
  label: string;
  description: string;
  suggestion: string;
}

// 舌诊打卡记录
export interface TongueCheckin {
  id: number;
  created_at: string;
  image_url: string;
  labels: string[];
  label_infos: TongueLabelResult[];
}

// 打卡响应数据
export interface TongueCheckinData {
  checkin: TongueCheckin;
}

// 舌诊打卡列表响应数据
export interface GetTongueListData {
  checkins: TongueCheckin[];
  total: number;
  page: number;
  page_size: number;
}

// 舌诊打卡列表请求参数
export interface GetTongueListParams {
  page: number;
  page_size: number;
}

// 带用户信息的打卡记录（医生端）
export interface TongueCheckinWithUser extends TongueCheckin {
  user_id: number;
  username: string;
}

// 所有用户打卡列表响应数据（医生端）
export interface GetAllTongueListData {
  checkins: TongueCheckinWithUser[];
  total: number;
  page: number;
  page_size: number;
}

// 舌象标签释义配置项（医生端）
export interface TongueLabelInfo {
  id: number;
  label: string;
  description: string;
  suggestion: string;
}

// 舌象标签释义配置列表响应数据（医生端）
export interface GetTongueLabelsData {
  labels: TongueLabelInfo[];
}

// 更新舌象标签释义请求参数（医生端）
export interface UpdateTongueLabelParams {
  label: string;
  description: string;
  suggestion: string;
}

// 拼接打卡图片的完整访问地址
export const tongueImageUrl = (imageUrl: string): string => {
  if (/^https?:\/\//.test(imageUrl)) {
    return imageUrl;
  }
  return `https://dk-backend.ibase.site${imageUrl}`;
};

// 通用请求函数
const request = async <T = any>(
  url: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> => {
  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  const data = await response.json();
  return data;
};

// API 方法
export const api = {
  // 登录
  login: (params: LoginParams): Promise<ApiResponse<AuthData>> =>
    request('/login', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  // 注册
  register: (params: RegisterParams): Promise<ApiResponse<AuthData>> =>
    request('/register', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  // 修改密码
  changePassword: (params: ChangePasswordParams): Promise<ApiResponse> =>
    request('/changepassword', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  // Ping 测试
  ping: (): Promise<ApiResponse> =>
    request('/ping', {
      method: 'GET',
    }),

  // 获取优惠券列表（需要认证）
  getDiscount: (token: string): Promise<ApiResponse<GetDiscountData>> =>
    request('/getdiscount', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }),

  // 使用优惠券（需要认证）
  useDiscount: (params: UseDiscountParams, token: string): Promise<ApiResponse> =>
    request('/usediscount', {
      method: 'POST',
      body: JSON.stringify(params),
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }),

  // 添加建议（需要认证）
  addAdvice: (params: AddAdviceParams, token: string): Promise<ApiResponse> =>
    request('/addadvice', {
      method: 'POST',
      body: JSON.stringify(params),
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }),

  // 获取建议列表（需要认证，仅医生）
  getAdviceList: (token: string): Promise<ApiResponse<GetAdviceListData>> =>
    request('/getadvicelist', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }),

  // 接受建议（需要认证，仅医生）
  acceptAdvice: (params: AcceptAdviceParams, token: string): Promise<ApiResponse> =>
    request('/acceptadvice', {
      method: 'POST',
      body: JSON.stringify(params),
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }),

  // 获取所有优惠券列表（需要认证，仅医生）
  getAllDiscountList: (params: GetAllDiscountListParams, token: string): Promise<ApiResponse<GetAllDiscountListData>> =>
    request(`/getalldiscountlist?page=${params.page}&page_size=${params.page_size}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }),

  // 获取所有用户列表（需要认证，仅医生）
  getAllUserList: (params: GetAllUserListParams, token: string): Promise<ApiResponse<GetAllUserListData>> => {
    const searchParam = params.search_by ? `&search_by=${encodeURIComponent(params.search_by)}` : '';
    return request(`/getalluserlist?page=${params.page}&page_size=${params.page_size}${searchParam}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
  },

  // 重置密码（需要认证，仅医生）
  resetPassword: (params: ResetPasswordParams, token: string): Promise<ApiResponse> =>
    request('/resetpassword', {
      method: 'POST',
      body: JSON.stringify(params),
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }),

  // 舌诊打卡：上传舌头照片并识别（需要认证，multipart 上传）
  tongueCheckin: async (file: File, token: string): Promise<ApiResponse<TongueCheckinData>> => {
    const formData = new FormData();
    formData.append('image', file);
    // 识别服务冷启动可能需要约 20 秒，前端不设过短超时
    const response = await fetch(`${API_BASE_URL}/tonguecheckin`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });
    return response.json();
  },

  // 获取当前用户舌诊打卡列表（需要认证）
  getTongueList: (params: GetTongueListParams, token: string): Promise<ApiResponse<GetTongueListData>> =>
    request(`/gettonguelist?page=${params.page}&page_size=${params.page_size}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }),

  // 获取所有用户舌诊打卡列表（需要认证，仅医生）
  getAllTongueList: (params: GetTongueListParams, token: string): Promise<ApiResponse<GetAllTongueListData>> =>
    request(`/getalltonguelist?page=${params.page}&page_size=${params.page_size}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }),

  // 获取舌象标签释义配置（需要认证，仅医生）
  getTongueLabels: (token: string): Promise<ApiResponse<GetTongueLabelsData>> =>
    request('/gettonguelabels', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }),

  // 更新舌象标签释义配置（需要认证，仅医生）
  updateTongueLabel: (params: UpdateTongueLabelParams, token: string): Promise<ApiResponse> =>
    request('/updatetonguelabel', {
      method: 'POST',
      body: JSON.stringify(params),
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }),
};

export default api; 